// Shared helpers for the AMC pending-transactions reminder. Used by the API (to
// gate both the automatic cadence and the manual send) and by the dashboard (to
// enable/disable the "send reminder" button and describe the last email), so
// every side agrees. Dependency-free on purpose: it only reads the two fields.

// Minimum gap between client-facing AMC reminders before another one may be sent.
// Spans both tracks: a recent email of either kind holds off the next one.
//  - a manual send holds the automatic cadence for this long, and
//  - any send holds the manual button for this long.
export const AMC_EMAIL_MIN_INTERVAL_HOURS = 48;

const MS_PER_HOUR = 60 * 60 * 1000;

// Minimal shape of the fields these helpers read. Both the API entity
// (QboCompaniesEntity) and the dashboard's QBOCompanyData satisfy it.
export interface AmcEmailTimestamps {
  last_amc_email?: { type: string; sentAt: string } | null;
  last_manual_amc_email?: {
    sentAt: string;
    sentBy: string;
    sentById: number;
  } | null;
}

// The most recent AMC email across both tracks, tagged with which it was and the
// detail needed to describe it.
export type LastAmcEmail =
  | { kind: 'automatic'; sentAt: string; type: string }
  | { kind: 'manual'; sentAt: string; sentBy: string; sentById: number };

// The single most recent AMC email across both tracks over all the client's
// companies. null when the client has never been emailed.
export const getLastAmcEmail = (
  companies: AmcEmailTimestamps[]
): LastAmcEmail | null => {
  let latest: LastAmcEmail | null = null;
  let latestMs = -Infinity;

  const consider = (candidate: LastAmcEmail) => {
    const ms = new Date(candidate.sentAt).getTime();
    if (!Number.isNaN(ms) && ms > latestMs) {
      latestMs = ms;
      latest = candidate;
    }
  };

  companies.forEach((company) => {
    const auto = company.last_amc_email;
    if (auto?.sentAt) {
      consider({ kind: 'automatic', sentAt: auto.sentAt, type: auto.type });
    }
    const manual = company.last_manual_amc_email;
    if (manual?.sentAt) {
      consider({
        kind: 'manual',
        sentAt: manual.sentAt,
        sentBy: manual.sentBy,
        sentById: manual.sentById,
      });
    }
  });

  return latest;
};

// Most recent moment the client was emailed about pending transactions, across
// both tracks. null when the client has never been emailed.
export const getLastAmcEmailSentAt = (
  companies: AmcEmailTimestamps[]
): Date | null => {
  const last = getLastAmcEmail(companies);
  return last ? new Date(last.sentAt) : null;
};

// Whether a new AMC reminder may be sent: true when the last one (automatic or
// manual) went out at least AMC_EMAIL_MIN_INTERVAL_HOURS ago, or there was none.
export const canSendAmcEmail = (
  companies: AmcEmailTimestamps[],
  now: Date = new Date()
): boolean => {
  const last = getLastAmcEmailSentAt(companies);
  if (!last) return true;
  return (
    now.getTime() - last.getTime() >= AMC_EMAIL_MIN_INTERVAL_HOURS * MS_PER_HOUR
  );
};
